import {
  collection,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  doc,
  limit,
  where,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, functions, httpsCallable, storage } from '../firebase';
import { getPreinvoiceRecipients, previewPreinvoiceMessage } from './preinvoiceUtils';
import {
  ensurePaymentEntryFromInvoice,
  ensureSaleEntryFromInvoice,
  syncAccountingForInvoiceStatus
} from './accountingUtils';

const SEND_INVOICES_FN = httpsCallable(functions, 'sendInvoices');

export const INVOICE_STATUSES = ['Sent', 'pending payment', 'Payed', 'sendError'];

export const SELECTABLE_PAYMENT_STATUSES = ['Sent', 'pending payment', 'Payed'];

export const INVOICE_EMAIL_FROM_NAME = 'TenisoUžsiėmimai.lt';

export const INVOICE_DEFAULT_SERVICE = 'Teniso treniruotės';

// Keep in sync with INVOICE_DEFAULT_MESSAGE_TEMPLATE in functions/index.js.
export const INVOICE_DEFAULT_MESSAGE_TEMPLATE = `Laba diena, {{vardas}},

Siunčiame sąskaitą faktūrą už suteiktas paslaugas. PDF failas laiško prisegtuke. Taip pat sąskaitą galite peržiūrėti paspaudę nuorodos mygtuką apačioje.

Suma: {{suma}}
Sąskaitos numeris: {{saskaitosNumeris}}
Paslaugų teikimo laikotarpis: {{paslauguLaikotarpis}}

{{pdfUrl}}

Linkime malonaus laiko aikštelėje!
${INVOICE_EMAIL_FROM_NAME}`;

export const INVOICE_TEMPLATE_PLACEHOLDERS = [
  { key: '{{vardas}}', description: 'kliento vardas' },
  { key: '{{pdfUrl}}', description: 'PDF nuoroda (HTML laiške rodoma kaip mygtukas „Atsisiųsti sąskaitą (PDF)")' },
  { key: '{{suma}}', description: 'suma (pvz. „40.00 Eur“)' },
  { key: '{{saskaitosNumeris}}', description: 'sąskaitos numeris' },
  { key: '{{paslauguLaikotarpis}}', description: 'paslaugų teikimo laikotarpis, neprivaloma (pvz. „2026-05-01 – 2026-05-31")' },
  { key: '{{data}}', description: 'metai-mėnuo (pvz. „2026-05“)' }
];


export const omitServicePeriodFromInvoiceTemplate = (template) => (
  String(template || '')
    .split('\n')
    .filter((line) => !/\{\{paslauguLaikotarpis\}\}/.test(line) && !/^Paslaugų teikimo laikotarpis:/.test(line.trim()))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
);

export const formatInvoiceServicePeriodLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  if (startDate === endDate) return startDate;
  return `${startDate} – ${endDate}`;
};

export { getPreinvoiceRecipients as getInvoiceRecipients, previewPreinvoiceMessage as previewInvoiceMessage };

export const sendInvoices = async ({
  amountEur,
  serviceDescription,
  recipients,
  isTest,
  messageTemplate,
  servicePeriodStart,
  servicePeriodEnd,
  sendEmail = true,
  paymentStatus
}) => {
  const resolvedStatus = SELECTABLE_PAYMENT_STATUSES.includes(paymentStatus) ? paymentStatus : 'Payed';
  const response = await SEND_INVOICES_FN({
    amountEur,
    serviceDescription: serviceDescription || INVOICE_DEFAULT_SERVICE,
    recipients,
    isTest: isTest === true,
    messageTemplate: typeof messageTemplate === 'string' ? messageTemplate : '',
    servicePeriodStart: typeof servicePeriodStart === 'string' ? servicePeriodStart : '',
    servicePeriodEnd: typeof servicePeriodEnd === 'string' ? servicePeriodEnd : '',
    sendEmail: sendEmail !== false,
    paymentStatus: resolvedStatus
  });
  const data = response?.data || { ok: false, results: [] };

  // Client-side Apskaita sync (idempotent). Never modifies invoice documents.
  try {
    const year = new Date().getFullYear();
    const results = Array.isArray(data?.results) ? data.results : [];
    for (const r of results) {
      if (!r?.invoiceDocId || r.status === 'sendError') continue;
      const invoice = {
        id: r.invoiceDocId,
        invoiceNumber: r.invoiceNumber,
        amountEur,
        serviceDescription: serviceDescription || INVOICE_DEFAULT_SERVICE,
        clientId: r.clientId,
        clientEmail: r.clientEmail,
        clientName: r.clientName,
        invoiceForCompany: r.invoiceForCompany,
        companyName: r.companyName,
        invoiceDate: new Date().toISOString().slice(0, 10),
        status: r.status || resolvedStatus,
        isTest: isTest === true
      };
      await ensureSaleEntryFromInvoice(invoice, year);
      if ((r.status || resolvedStatus) === 'Payed') {
        await ensurePaymentEntryFromInvoice(invoice, year);
      }
    }
  } catch (err) {
    console.error('Accounting sync after sendInvoices failed:', err);
  }

  return data;
};

export const subscribeToRecentInvoices = (year, callback) => {
  const yearRef = collection(db, 'invoices', String(year), 'items');
  const q = query(yearRef, orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (error) => {
    console.error('invoices snapshot error:', error);
    callback([]);
  });
};

/** Client view: invoices assigned to this user (by email or auth uid). */
export const subscribeToClientInvoices = (year, clientEmail, clientUid, callback) => {
  const normalizedEmail = String(clientEmail || '').trim().toLowerCase();
  if (!normalizedEmail && !clientUid) {
    callback([]);
    return () => {};
  }
  const yearRef = collection(db, 'invoices', String(year), 'items');
  const q = normalizedEmail
    ? query(yearRef, where('clientEmail', '==', normalizedEmail), orderBy('createdAt', 'desc'), limit(100))
    : query(yearRef, where('clientId', '==', clientUid), orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error('client invoices snapshot error:', error);
    callback([]);
  });
};

export const updateInvoiceStatus = async (year, docId, status) => {
  if (!INVOICE_STATUSES.includes(status)) {
    throw new Error('Neteisingas statusas');
  }
  const itemRef = doc(db, 'invoices', String(year), 'items', docId);
  const snap = await getDoc(itemRef);
  const previousStatus = snap.exists() ? snap.data()?.status : null;
  const invoice = snap.exists() ? { id: docId, ...snap.data() } : { id: docId };

  await updateDoc(itemRef, { status });

  try {
    await syncAccountingForInvoiceStatus(year, { ...invoice, status }, previousStatus, status);
  } catch (err) {
    console.error('Accounting sync after invoice status change failed:', err);
  }
};

const parseInvoiceSequence = (item) => {
  const direct = Number(item?.sequence);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(item?.invoiceNumber || '').match(/^(?:SF|PR)-(\d{4})-(\d+)$/i);
  if (!match) return null;
  const seq = Number(match[2]);
  return Number.isFinite(seq) && seq > 0 ? seq : null;
};

/** Return deleted SF number to the pool so the next invoice can reuse it. */
export const releaseInvoiceSequence = async (year, sequence) => {
  const seq = Number(sequence);
  if (!Number.isFinite(seq) || seq <= 0) return;

  const counterRef = doc(db, 'counters', `invoice_${year}`);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const data = snap.exists() ? (snap.data() || {}) : {};
    let lastNumber = Number(data.lastNumber || 0);
    let freeSequences = Array.isArray(data.freeSequences)
      ? data.freeSequences.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [];

    if (seq > lastNumber) {
      return;
    }
    freeSequences.push(seq);
    freeSequences = [...new Set(freeSequences)].sort((a, b) => a - b);

    // Compact trailing frees: deleting the highest SF lowers lastNumber.
    while (lastNumber > 0 && freeSequences.includes(lastNumber)) {
      freeSequences = freeSequences.filter((n) => n !== lastNumber);
      lastNumber -= 1;
    }

    tx.set(counterRef, {
      lastNumber,
      freeSequences,
      updatedAt: Timestamp.now()
    }, { merge: true });
  });
};

export const deleteInvoiceEntry = async (year, item) => {
  const docId = item?.id;
  if (!docId) {
    throw new Error('Trūksta sąskaitos įrašo ID');
  }

  const tasks = [];

  if (item?.pdfStoragePath) {
    tasks.push(
      deleteObject(ref(storage, item.pdfStoragePath)).catch((error) => {
        if (error?.code !== 'storage/object-not-found') {
          console.warn('Skipping PDF delete for invoice:', error?.code || error?.message || error);
        }
      })
    );
  }

  tasks.push(deleteDoc(doc(db, 'invoices', String(year), 'items', docId)));

  const mailQ = query(collection(db, 'mail'), where('reservationId', '==', docId));
  const mailSnap = await getDocs(mailQ);
  mailSnap.forEach((mailDoc) => {
    tasks.push(deleteDoc(mailDoc.ref));
  });

  await Promise.all(tasks);

  // Reuse SF number for the next non-test invoice (test invoices do not consume sequence).
  if (item?.isTest !== true) {
    const sequence = parseInvoiceSequence(item);
    if (sequence) {
      try {
        await releaseInvoiceSequence(year, sequence);
      } catch (err) {
        console.warn('Failed to release invoice sequence:', err?.message || err);
      }
    }
  }
};
